import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import styles from "./Rbac.module.css";
import Loader from "../../../shared/Component/Loader";
import { IconButton } from "@mui/material";
import { AddCircleRounded, RemoveCircleRounded } from "@mui/icons-material";
import { employeeRoutes } from "../../../AppConfig";
import { AxiosInstance } from "../../../axios";

export default function EditPage() {
  const navigate = useNavigate();
  const { user_id } = useParams();

  const [employeeInfo, setEmployeeInfo] = useState({});
  const [initialFields, setInitialFields] = useState({
    Title: "",
    Description: "",
  });
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [updateListData, setUpdateListData] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [pageId, setPageId] = useState("");
  const [expandedIds, setExpandedIds] = useState([]);
  const txtBSearch = useRef(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("success");

  const closeModal = () => setShowModal(false);
  const moduleOrder = {
    "Admission List": 1,
    Batch: 2,
    Faculty: 3,
    "Test List": 4,
    "Registered Users": 5,
    "Course Management": 6,
    Announcements: 7,
    "Coupon Code": 8,
    "Study Materials": 9,
    Gallery: 10,
    "Faculty Profile": 11,
    "Exam Centers": 12,
    "Student List": 13,
    "Students Query": 14,
    "Cash Payments": 15,
    "Class List": 16,
    "Panel List": 17,
    "Interview List": 18,
  };

  // Fetch employee + page details
  useEffect(() => {
    const fetchPageAccessDetails = async () => {
      if (!user_id) return;
      try {
        setIsLoading(true);
        const response = await AxiosInstance.post("/api/menu/findById", {
          user_id,
        });
        const data = response?.data?.data;
        if (data) {
          const employee = data.basicDetails?.[0] || {};
          const pageInfo = data.pageDetails?.[0] || {};
          setEmployeeInfo(employee);
          setInitialFields({
            Title: pageInfo?.title || "",
            Description: pageInfo?.description || "",
          });
          setPageId(pageInfo?.id || "");

          // ---- Handle menu_id as array or string ----
          let existingMenuIds = [];
          if (Array.isArray(pageInfo.menu_id)) {
            existingMenuIds = pageInfo.menu_id.map((id) => Number(id));
          } else if (typeof pageInfo.menu_id === "string") {
            existingMenuIds = pageInfo.menu_id
              .split(",")
              .map((id) => Number(id))
              .filter((id) => !isNaN(id));
          }

          setSelectedMenuIds(existingMenuIds);

          // ---- Build module structure ----
          const uniqueRoutes = Object.values(
            employeeRoutes.reduce((acc, route) => {
              if (!acc[route.id]) acc[route.id] = route;
              return acc;
            }, {})
          );

          const groupedModules = {};
          uniqueRoutes.forEach((route) => {
            if (route.module === "AdminAccess") return; // Skip AdminAccess
            const moduleName = route.module || "Dashboard";
            if (!groupedModules[moduleName]) {
              groupedModules[moduleName] = {
                id: moduleName,
                name: moduleName,
                page: "All",
                checked: false,
                subPages: [],
              };
            }
            groupedModules[moduleName].subPages.push({
              id: route.id,
              name: route.module || "Dashboard",
              page: route.pages || route.page || " ",
              order: route.order,
              disable: route.disable,
              checked: existingMenuIds.includes(Number(route.id)),
            });
          });

          // --- Enhance with dependency enable logic ---
          const formattedList = Object.values(groupedModules).map((item) => {
            // if any subpage of this module has order "1" checked → enable all subpages
            const hasPrimaryChecked = item.subPages.some(
              (s) => s.order === "1" && s.checked
            );

            const updatedSubPages = item.subPages.map((s) => {
              // enable Report Download (CSV / PDF) etc. when List(order 1) is selected
              if (hasPrimaryChecked) {
                return { ...s, disable: false };
              }
              return s;
            });

            const allChecked = updatedSubPages.every((s) => s.checked);
            return { ...item, subPages: updatedSubPages, checked: allChecked };
          });
          const sortedModules = formattedList.sort((a, b) => {
            const orderA = moduleOrder[a.name] || 999;
            const orderB = moduleOrder[b.name] || 999;
            return orderA - orderB;
          });
          setUpdateListData(sortedModules);
          setFilteredData(sortedModules);

          //  Auto-expand only modules with checked subpages
          const initiallyExpanded = sortedModules
            .filter((item) => item.subPages.some((s) => s.checked))
            .map((item) => item.id);
          setExpandedIds(initiallyExpanded);
        }
      } catch (error) {
        console.error("Error fetching page access:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPageAccessDetails();
  }, [user_id]);

  // Keep checkboxes synced when selectedMenuIds changes
  useEffect(() => {
    setUpdateListData((prevList) =>
      prevList.map((module) => {
        const updatedSubPages = module.subPages.map((sub) => ({
          ...sub,
          checked: selectedMenuIds.includes(Number(sub.id)),
        }));
        const allChecked = updatedSubPages.every((s) => s.checked);
        return { ...module, subPages: updatedSubPages, checked: allChecked };
      })
    );
  }, [selectedMenuIds]);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);
  // Keep checkboxes synced when selectedMenuIds changes
  useEffect(() => {
    setUpdateListData((prevList) =>
      prevList.map((module) => {
        const updatedSubPages = module.subPages.map((sub) => ({
          ...sub,
          checked: selectedMenuIds.includes(Number(sub.id)),
        }));
        const allChecked = updatedSubPages.every((s) => s.checked);
        return { ...module, subPages: updatedSubPages, checked: allChecked };
      })
    );
  }, [selectedMenuIds]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInitialFields((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  // Expand row
  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  //  Parent checkbox logic
  const handleParentCheckbox = (parentId) => {
    setUpdateListData((prevList) =>
      prevList.map((item) => {
        if (item.id === parentId) {
          const newChecked = !item.checked;

          const updatedSubs = item.subPages.map((sub) => {
            if (newChecked) {
              // Parent checked → enable and check all
              return { ...sub, checked: true, disable: false };
            } else {
              // Parent unchecked → disable Report Download, uncheck all
              if (sub.page === "Report Download (CSV / PDF)") {
                return { ...sub, checked: false, disable: true };
              }
              return { ...sub, checked: false };
            }
          });

          // Update selectedMenuIds
          const subIds = updatedSubs.map((s) => Number(s.id));
          setSelectedMenuIds((prev) => {
            if (newChecked) {
              return [...new Set([...prev, ...subIds])];
            } else {
              return prev.filter((id) => !subIds.includes(id));
            }
          });

          return { ...item, checked: newChecked, subPages: updatedSubs };
        }
        return item;
      })
    );
    setFilteredData((prevList) =>
      prevList.map((item) => {
        if (item.id === parentId) {
          const newChecked = !item.checked;

          const updatedSubs = item.subPages.map((sub) => {
            if (newChecked) {
              // Parent checked → enable and check all
              return { ...sub, checked: true, disable: false };
            } else {
              // Parent unchecked → disable Report Download, uncheck all
              if (sub.page === "Report Download (CSV / PDF)") {
                return { ...sub, checked: false, disable: true };
              }
              return { ...sub, checked: false };
            }
          });

          // Update selectedMenuIds
          const subIds = updatedSubs.map((s) => Number(s.id));
          setSelectedMenuIds((prev) => {
            if (newChecked) {
              return [...new Set([...prev, ...subIds])];
            } else {
              return prev.filter((id) => !subIds.includes(id));
            }
          });

          return { ...item, checked: newChecked, subPages: updatedSubs };
        }
        return item;
      })
    );
  };

  //  Subpage checkbox logic
  const handleSubCheckbox = (parentId, subId) => {
    setUpdateListData((prevList) => {
      const updatedList = prevList.map((module) => {
        if (module.id !== parentId) return module;

        // Toggle clicked subpage
        const updatedSubPages = module.subPages.map((sub) =>
          sub.id === subId ? { ...sub, checked: !sub.checked } : sub
        );

        // Handle dependency rule: List → Report Download
        const listSub = updatedSubPages.find((s) => s.page === "List");
        const reportSub = updatedSubPages.find(
          (s) => s.page === "Report Download (CSV / PDF)"
        );

        if (reportSub) {
          if (listSub?.checked) {
            reportSub.disable = false;
          } else {
            reportSub.disable = true;
            reportSub.checked = false;
          }
        }

        //  Determine parent checkbox state (only check if ALL subpages are checked)
        const allChecked = updatedSubPages.every((s) => s.checked);

        return {
          ...module,
          checked: allChecked, // strictly true if all subpages checked
          subPages: updatedSubPages,
        };
      });

      // Compute new selectedMenuIds once
      const newSelectedIds = updatedList.flatMap((m) =>
        m.subPages.filter((s) => s.checked).map((s) => Number(s.id))
      );

      setSelectedMenuIds([...new Set(newSelectedIds)]);
      return updatedList;
    });

    setFilteredData((prevList) => {
      const updatedList = prevList.map((module) => {
        if (module.id !== parentId) return module;

        // Toggle clicked subpage
        const updatedSubPages = module.subPages.map((sub) =>
          sub.id === subId ? { ...sub, checked: !sub.checked } : sub
        );

        // Handle dependency rule: List → Report Download
        const listSub = updatedSubPages.find((s) => s.page === "List");
        const reportSub = updatedSubPages.find(
          (s) => s.page === "Report Download (CSV / PDF)"
        );

        if (reportSub) {
          if (listSub?.checked) {
            reportSub.disable = false;
          } else {
            reportSub.disable = true;
            reportSub.checked = false;
          }
        }

        //  Determine parent checkbox state (only check if ALL subpages are checked)
        const allChecked = updatedSubPages.every((s) => s.checked);

        return {
          ...module,
          checked: allChecked, // strictly true if all subpages checked
          subPages: updatedSubPages,
        };
      });

      // Compute new selectedMenuIds once
      const newSelectedIds = updatedList.flatMap((m) =>
        m.subPages.filter((s) => s.checked).map((s) => Number(s.id))
      );

      // setSelectedMenuIds([...new Set(newSelectedIds)]);
      return updatedList;
    });
  };

  // Search logic
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (val.trim() === "") setUpdateListData(filteredData);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter" || e.type === "click") {
      const term = searchTerm.trim().toLowerCase();
      if (term === "") {
        setUpdateListData(updateListData);
      } else {
        const filtered = updateListData.filter((item) =>
          item.name.toLowerCase().includes(term)
        );
        setUpdateListData(filtered);
      }
    }
  };

  // Confirm save
  const handleSave = () => {
    let hasError = false;
    const newErrors = {};

    if (!initialFields.Title.trim()) {
      newErrors.Title = "Title is required";
      hasError = true;
    }
    if (!initialFields.Description.trim()) {
      newErrors.Description = "Description is required";
      hasError = true;
    }

    setError(newErrors);

    // Focus the first invalid input
    if (hasError) {
      if (newErrors.Title && document.querySelector("[name='Title']")) {
        document.querySelector("[name='Title']").focus();
      } else if (
        newErrors.Description &&
        document.querySelector("[name='Description']")
      ) {
        document.querySelector("[name='Description']").focus();
      }
      return;
    }

    setModalType("confirm");
    setModalMessage(
      "Are you sure you want to save the selected page access to the employee?"
    );
    setShowModal(true);
  };

  // Actual API save
  const handleSaveConfirmed = async () => {
    try {
      setIsLoading(true);
      const payload = {
        id: pageId || "",
        user_id,
        title: initialFields.Title,
        description: initialFields.Description,
        menu_id: selectedMenuIds.map((id) => Number(id)), //  save as array
      };

      await AxiosInstance.post("/api/menu/add", payload);
      navigate(-1);
    } catch (err) {
      console.error("Error saving access:", err);
      setModalType("error");
      setModalMessage("An error occurred while saving.");
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateBack = () => navigate(-1);

  // ---------------- JSX ----------------
  return isLoading ? (
    <div
      className="loaderApi"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        zIndex: 999,
      }}
    >
      <Loader />
    </div>
  ) : (
    <div className="container-fluid global_container p-0">
      <div className="BathHeader">
        <div className="row">
          <div className="col-md-6">
            <h2 className="TitleHeader">Edit Page Access</h2>
          </div>
          <div className="col-md-6 ListBtn">
            <ul className="List-Button">
              <li className="ListBtnItems">
                <Link
                  to="javascript:void(0)"
                  onClick={navigateBack}
                  className="secondaryButton"
                >
                  Back to View Page Access
                </Link>
              </li>
              <li className="ListBtnItems">
                <Link
                  to="javascript:void(0)"
                  onClick={handleSave}
                  className="primaryButton text-center"
                >
                  Save Page Access
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Employee Info */}
        <div className="row p-2 py-0">
          <fieldset className="mt-3">
            <legend className="w-auto">Employee Details</legend>
            <div className="row mb-3">
              {[
                ["Name", employeeInfo.fullname],
                ["Employee ID", employeeInfo.faculty_id],
                ["Email", employeeInfo.email],
                ["Phone Number", employeeInfo.mobile_no],
                ["Employee Type", employeeInfo.emp_type],
                ["Location", employeeInfo.location],
              ].map(([label, value], i) => (
                <div key={i} className="my-1 col-lg-3 col-md-3 col-12">
                  <p className="confirm_label m-0 p-0">{label}</p>
                  <p className="confirm_value my-1 m-0 p-0">{value || "_"}</p>
                </div>
              ))}
            </div>
          </fieldset>

          {/* Title + Description */}
          <fieldset className="mt-3">
            <legend className="w-auto">Title</legend>
            <form className="row g-3">
              <div className="col-md-6">
                <label htmlFor="Title" className="form-label confirm_value">
                  Title
                </label>
                <input
                  className="desc_input_text w-100 rounded-2 input-font-size-sm"
                  placeholder="Enter the title"
                  name="Title"
                  value={initialFields.Title}
                  onChange={handleChange}
                />
                {error.Title && (
                  <small className="error_message">{error.Title}</small>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label confirm_value">Description</label>
                <input
                  className="desc_input_text w-100 rounded-2 input-font-size-sm"
                  placeholder="Enter the description"
                  name="Description"
                  value={initialFields.Description}
                  onChange={handleChange}
                />
                {error.Description && (
                  <small className="error_message">{error.Description}</small>
                )}
              </div>
            </form>
          </fieldset>

          {/* Page Access Table */}
          <fieldset className="mt-3">
            <legend className="w-auto">Page Access Details</legend>
            <div className="row mt-3">
              <div className="col-lg-4 col-md-4 col-sm-12 mb-2">
                <div className="input-group">
                  <input
                    ref={txtBSearch}
                    type="text"
                    className={styles.Inputs}
                    placeholder="Search by Module"
                    value={searchTerm}
                    onChange={handleSearch}
                    onKeyDown={handleSearchSubmit}
                  />
                  <span
                    className={styles.input_group_text}
                    onClick={handleSearchSubmit}
                  >
                    <span className="material-icons">search</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-12 table-responsive tableMinHeight p-0">
              <table className="table" id="tableListHeader">
                <thead className={styles.permission_column}>
                  <tr>
                    <th>S.No</th>
                    <th>Module</th>
                    <th>Page Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {updateListData.map((item, index) => {
                    const hasCheckedSubpages = item.subPages.some(
                      (s) => s.checked
                    );
                    const isExpanded = expandedIds.includes(item.id);

                    return (
                      <React.Fragment key={item.id}>
                        <tr>
                          <td>
                            <div className="d-flex align-items-center">
                              <IconButton
                                size="small"
                                disableRipple
                                style={{
                                  color: "#808080",
                                  padding: "4px",
                                  marginRight: "6px",
                                }}
                                onClick={() =>
                                  setExpandedIds((prev) =>
                                    prev.includes(item.id)
                                      ? prev.filter((id) => id !== item.id)
                                      : [...prev, item.id]
                                  )
                                }
                              >
                                {isExpanded ? (
                                  <RemoveCircleRounded fontSize="small" />
                                ) : (
                                  <AddCircleRounded fontSize="small" />
                                )}
                              </IconButton>
                              {index + 1}
                            </div>
                          </td>
                          <td>{item.name}</td>
                          <td>{item.page}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={item.subPages.every((s) => s.checked)}
                              onChange={() => handleParentCheckbox(item.id)}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                                accentColor: "#007bff",
                              }}
                            />
                          </td>
                        </tr>
                        {isExpanded &&
                          item.subPages.map((sub) => (
                            <tr key={sub.id}>
                              <td></td>
                              <td>{sub.name}</td>
                              <td>{sub.page}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={sub.checked}
                                  onChange={() =>
                                    handleSubCheckbox(item.id, sub.id)
                                  }
                                  disabled={sub.disable}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                    accentColor: "#007bff",
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                  {updateListData.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No modules found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </fieldset>
        </div>
      </div>

      {/* Modal */}
      <Modal show={showModal} onHide={closeModal} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {modalType === "confirm"
              ? "Confirm Page Access"
              : modalType === "success"
              ? "Success"
              : "Error"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p
            className={`text-${
              modalType === "error"
                ? "danger"
                : modalType === "success"
                ? "success"
                : "dark"
            } fs-6 mb-0`}
          >
            {modalMessage}
          </p>
        </Modal.Body>

        <Modal.Footer>
          {modalType === "confirm" ? (
            <>
              <button
                className="secondaryButton bg-transparent border-0 mx-2"
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className="primaryButton border-0 mx-2"
                onClick={() => {
                  setShowModal(false);
                  handleSaveConfirmed();
                }}
              >
                Yes, Assign
              </button>
            </>
          ) : (
            <button
              className="primaryButton border-0 mx-2"
              onClick={closeModal}
            >
              OK
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
